import Ionicons from "@expo/vector-icons/Ionicons";
import { useFocusEffect, useRouter, type Href } from "expo-router";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppState, Platform, Pressable, StyleSheet, View } from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loading } from "@/components/ui/loading";
import { Screen } from "@/components/ui/screen";
import { Radius, Spacing } from "@/constants/theme";
import { peekDestination } from "@/lib/pending-destination";
import { useTheme } from "@/hooks/use-theme";
import { locationStrings } from "@/i18n/features/location.i18n";
import { notify } from "@/lib/dialog";
import {
  getLocationAccessSteps,
  getWebEnvironment,
  openAppSettings,
  openLocationSettings,
  requestLocationAccess,
  type LocationAccess,
} from "@/lib/location-access";
import { locationRetryAction } from "@/lib/location-retry";
import {
  getNotificationPermission,
  registerForPushNotifications,
} from "@/lib/notifications";
import { notificationCardState } from "@/lib/push-setup-outcome";
import { useAuthStore } from "@/state/auth-store";
import { useLocationAccessStore } from "@/state/location-access-store";

/**
 * `denied` still allows another OS prompt; `blocked` means the OS has stopped
 * asking and only its settings page can change the answer. Keeping them apart
 * is what decides between re-prompting and sending the user to settings — a
 * request in the blocked state resolves instantly with no prompt, which reads
 * to the user as the button doing nothing.
 */
type State = "idle" | "granted" | "denied" | "blocked";

const INITIAL_PERMISSION_CHECK_TIMEOUT_MS = 4000;

export default function Permissions() {
  const router = useRouter();
  const { t } = useTranslation();
  const c = useTheme();
  const completePermissions = useAuthStore((s) => s.completePermissions);

  // Shared with the navigation guard, so what this screen shows and what the
  // guard lets past can never disagree.
  const access = useLocationAccessStore((s) => s.access);
  const setAccess = useLocationAccessStore((s) => s.setAccess);
  const refreshAccess = useLocationAccessStore((s) => s.refresh);

  const [notifications, setNotifications] = useState<State>("idle");
  // One permission at a time. Both cards on one screen buried the location
  // decision — the one choice this flow exists for — under a second card and
  // its own explanatory copy. The location step ends with its quiet "Not now",
  // and notifications get the next screen to themselves.
  const [step, setStep] = useState<"location" | "notifications">("location");
  // A card the user has not touched stays on its inviting CTA even if the OS
  // already says no — the request handler routes a blocked permission to
  // settings anyway, and opening with "Open settings" would accuse the user of
  // a refusal they have not made in this app.
  const [locationTouched, setLocationTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [initialPermissionsChecked, setInitialPermissionsChecked] =
    useState(false);

  const isWeb = Platform.OS === "web";
  const webEnvironment = useMemo(() => getWebEnvironment(), []);

  // Both permissions already granted means this screen has nothing to ask —
  // every card would render "Allowed" over a Continue button, which is a form
  // with no questions. Seen on web, where the browser keeps site permissions
  // across sign-ins, so a returning user hit this screen with both cards
  // already green. Complete the step and move on. Once: `finish()` is a
  // network write, and the effect re-fires as its own state changes.
  //
  // The push token is not lost by skipping: the tabs layout re-registers it
  // (requestPermission: false) on entry and on every foreground.
  const autoCompleted = useRef(false);
  const shouldAutoComplete = access === "ready" && notifications === "granted";
  useEffect(() => {
    if (autoCompleted.current || busy) return;
    if (!initialPermissionsChecked || !shouldAutoComplete) return;
    autoCompleted.current = true;
    void finish();
    // `finish` is a plain component function; the guards above are the real
    // dependency contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPermissionsChecked, shouldAutoComplete, busy]);

  // Location granted means this step has no question left, so show the one that
  // does. Derived rather than set in an effect, so there is no state to keep in
  // sync with the permission.
  //
  // This used to be gated on `!locationTouched`, to keep a grant made from the
  // card itself from yanking the screen forward under the user's finger; the
  // user was expected to leave via a Continue button. That button does not
  // exist — a granted card renders "Allowed" and no action at all — so the only
  // control left was the muted "Not now", and the way forward from a permission
  // the user had just ALLOWED was a button that reads like declining it.
  // Observed on Safari, 2026-09-05.
  //
  // The yank it guarded against is not what happens either: the card's button
  // opens the browser's own permission prompt, so at the moment `access` turns
  // ready the user's attention is on that dialog's Allow, not on this screen.
  const effectiveStep =
    step === "location" && access === "ready" ? "notifications" : step;

  // The OS never tells the app that a permission was changed in its settings,
  // so the only way to notice is to look again when we come back. Without this,
  // granting in settings left the card stuck on its refused state with no way
  // out but restarting the app. Location is watched app-wide, so only
  // notifications need catching up here.
  const syncFromSystem = useCallback(async () => {
    try {
      const next = await getNotificationPermission();
      if (next.status !== "granted") {
        setNotifications((previous) =>
          notificationCardState({
            previous,
            outcome: "denied",
            canAskAgain: next.canAskAgain,
            passive: true,
          }),
        );
        return;
      }
      // Permission alone is not delivery. Asking the OS whether we are allowed
      // says nothing about whether this device is actually registered, and
      // reading "granted" straight off the permission is what let a refused
      // registration go back to showing "Allowed" on every refocus — and
      // auto-complete the step. `requestPermission: false`, so this never
      // opens a prompt; it is a no-op once the device is registered.
      const outcome = await registerForPushNotifications({
        requestPermission: false,
      });
      setNotifications((previous) =>
        notificationCardState({
          previous,
          outcome,
          canAskAgain: next.canAskAgain,
          passive: true,
        }),
      );
    } catch {
      // Leave whatever the last explicit request produced.
    }
  }, []);

  // On focus for the mount, and on foreground for the trip out to OS settings
  // and back — the latter does not re-focus the route, so both are needed.
  useFocusEffect(
    useCallback(() => {
      let active = true;
      const timeout = setTimeout(() => {
        if (active) setInitialPermissionsChecked(true);
      }, INITIAL_PERMISSION_CHECK_TIMEOUT_MS);

      void Promise.allSettled([refreshAccess(), syncFromSystem()]).finally(() => {
        clearTimeout(timeout);
        if (active) setInitialPermissionsChecked(true);
      });

      const subscription = AppState.addEventListener("change", (next) => {
        if (next === "active") void syncFromSystem();
      });

      return () => {
        active = false;
        clearTimeout(timeout);
        subscription.remove();
      };
    }, [refreshAccess, syncFromSystem]),
  );

  async function askLocation() {
    setLocationTouched(true);

    // One decision table, shared with the home screen's Try again — the two
    // presses that may put a permission prompt on screen must never disagree
    // about when that is possible.
    switch (locationRetryAction(access, isWeb)) {
      case "request":
        // Straight into the request, with nothing awaited in front of it: on
        // web the browser only shows its prompt while the press is still the
        // reason anything is happening.
        setAccess(await requestLocationAccess());
        return;

      case "open-app-settings":
        // The OS has stopped asking, so a request here would resolve instantly
        // with no prompt — which reads as a dead button. Only its settings page
        // can change the answer.
        await openAppSettings();
        return;

      case "open-location-settings":
        // The device-wide switch, which is a different screen from this app's
        // permissions.
        await openLocationSettings();
        return;

      case "show-steps":
        // The steps are already on this screen, under the card. Looking again
        // is all that is left once the user has acted on them.
        await refreshAccess();
        return;

      case "refresh":
        // Ready needs nothing; a failed check has nothing to do but look again.
        if (access === "ready") return;
        await refreshAccess();
        return;
    }
  }

  async function askNotifications() {
    try {
      const current = await getNotificationPermission();
      if (current.status === "granted") {
        // Already allowed but the card said otherwise — most likely the earlier
        // registration failed. Re-run it and let ITS outcome decide. Awaited,
        // and no longer setting "granted" up front: this is the "Try again"
        // press a refused browser makes, and claiming success before the
        // attempt returned is what made the refusal evaporate on one tap.
        const outcome = await registerForPushNotifications({
          requestPermission: false,
        });
        setNotifications((previous) =>
          notificationCardState({
            previous,
            outcome,
            canAskAgain: current.canAskAgain,
            passive: false,
          }),
        );
        return;
      }

      if (!current.canAskAgain) {
        setNotifications("blocked");
        // `openAppSettings` is `Linking.openSettings`, which no browser
        // implements: on web it throws, gets caught, logs, and returns false —
        // a button that does nothing at all. The same dead press as the
        // location card's (Safari, 2026-09-05), reached whenever a browser has
        // refused notifications, since a refused site cannot prompt again.
        //
        // Looking again is the only thing left that can change the answer, and
        // it is what the user needs after flipping the setting in the browser.
        if (isWeb) {
          await syncFromSystem();
          return;
        }
        await openAppSettings();
        return;
      }

      const result = await registerForPushNotifications();
      // Whether we may ask a second time is the OS's call, not a count we keep
      // ourselves — and it is only worth asking when something was refused.
      const canAskAgain =
        result === "registered"
          ? false
          : (await getNotificationPermission()).canAskAgain;
      // One mapping for every writer. A registration the server refused lands
      // on "denied" (Try again, no claim about the OS) rather than "granted",
      // which would falsely promise "know when someone sends you a cue", or
      // "blocked", which would send the user to correct settings.
      setNotifications((previous) =>
        notificationCardState({
          previous,
          outcome: result,
          canAskAgain,
          passive: false,
        }),
      );
    } catch {
      setNotifications("denied");
    }
  }

  async function finish() {
    setBusy(true);
    try {
      // Read BEFORE the await, not after: completing permissions flips
      // `permissionsDone`, the central guard resumes the invitation first,
      // and ARRIVING there clears the slot — so a peek after the await reads
      // null and this replace would stomp the resume with "home". Captured
      // here, both navigators name the same destination and the race is
      // harmless whoever wins. Proven with the browser harness 2026-09-04:
      // the after-the-await read reached the invitation and then navigated
      // away from it.
      const pending = peekDestination();
      await completePermissions();
      router.replace(pending ? (pending as Href) : "/(tabs)/home");
    } catch {
      // `completePermissions` is a network write, and this button is pressed
      // from venue wifi by design. Without this catch the rejection escaped the
      // handler entirely: a red-box in dev, and in production the spinner
      // simply stopped — the one button that lets someone proceed in limited
      // mode looked like it did nothing. Same dialog every other screen uses;
      // the local permission state is untouched, so pressing again just works.
      notify(t("errors.generic"), t("errors.tryAgainInAMoment"));
    } finally {
      setBusy(false);
    }
  }

  const ready = access === "ready";

  // Only two of these are a refusal. The rest are a browser or a device that
  // cannot produce a position at all, which is nobody's decision.
  const locationState: State = ready
    ? "granted"
    : !locationTouched
      ? "idle"
      : access === "permission-blocked"
        ? "blocked"
        : "denied";

  const steps = locationTouched
    ? getLocationAccessSteps(access, webEnvironment, t)
    : undefined;
  const checkingPermissions =
    !initialPermissionsChecked ||
    (shouldAutoComplete && (!autoCompleted.current || busy));
  const skipAction = (
    <Pressable
      accessibilityRole="button"
      disabled={busy}
      onPress={() =>
        effectiveStep === "location" ? setStep("notifications") : void finish()
      }
      style={({ pressed }) => [
        styles.skipAction,
        pressed && styles.skipActionPressed,
        busy && styles.skipActionDisabled,
      ]}
    >
      <ThemedText variant="label" tone="muted" style={styles.skipText}>
        {t("common.notNow")}
      </ThemedText>
    </Pressable>
  );

  return (
    <Screen scroll>
      <View style={styles.body}>
        <View style={styles.header}>
          <ThemedText variant="title">{t("onboarding.permissionsTitle")}</ThemedText>
          <ThemedText variant="body" tone="secondary">
            {t("onboarding.permissionsSubtitle")}
          </ThemedText>
        </View>

        {checkingPermissions ? (
          <Loading style={styles.permissionCheck} />
        ) : (
          <View style={styles.promptStack}>
            {effectiveStep === "location" ? (
              <PermissionCard
                icon="location-outline"
                title={t("onboarding.locationTitle")}
                subtitle={t("onboarding.locationRequirement")}
                state={locationState}
                description={locationDescription({
                  access,
                  touched: locationTouched,
                  isWeb,
                  browser: webEnvironment.browserName,
                  isMac: webEnvironment.isMac,
                  t,
                })}
                actionLabel={locationActionLabel({
                  access,
                  touched: locationTouched,
                  isWeb,
                  t,
                })}
                steps={steps}
                onPress={() => void askLocation()}
                color={c.tint}
                tint={c.backgroundSelected}
              />
            ) : (
              <PermissionCard
                icon="notifications-outline"
                title={t("onboarding.notificationsTitle")}
                state={notifications}
                description={t("onboarding.notificationsDescription")}
                actionLabel={
                  notifications === "blocked"
                    ? // Web has no settings page this app can open, so the
                      // press looks again instead — and says so.
                      isWeb
                      ? t("common.tryAgain")
                      : t("common.openSettings")
                    : notifications === "denied"
                      ? t("common.tryAgain")
                      : t("onboarding.notificationsAction")
                }
                onPress={() => void askNotifications()}
                color={c.tint}
                tint={c.backgroundSelected}
              />
            )}

            {/*
              Location is offered, never demanded. Blocking Not now on it turned
              one tap into an account that could never be used at all. The card's
              action is the real permission CTA; Not now is the quiet way past
              the ask, close enough to read as part of the same decision.

              Nothing downstream needs relaxing. Everything that genuinely needs a
              position already refuses without one and says why — `join` and
              `setVisible` raise VisibilityNeedsLocationException, Small Groups
              refuse create and join with "Become visible…", and a Cue requires
              both sides visible. Browsing while hidden is deliberately ungated.
            */}
            {skipAction}

            {effectiveStep === "location" && locationTouched && !ready ? (
              <ThemedText variant="caption" tone="muted">
                {t(locationStrings.requiredFooter)}
              </ThemedText>
            ) : null}
          </View>
        )}
      </View>
    </Screen>
  );
}

/**
 * What the location card says. Only the first case is the ordinary one — the
 * rest are the ways location can be unavailable that an in-app prompt cannot
 * fix, each of which needs to name what is actually wrong.
 */
function locationDescription({
  access,
  touched,
  isWeb,
  browser,
  isMac,
  t,
}: {
  access: LocationAccess;
  touched: boolean;
  isWeb: boolean;
  browser: string;
  isMac: boolean;
  t: TFunction;
}): string {
  if (!touched) return t("onboarding.locationDescription");

  switch (access) {
    case "permission-blocked":
      return isWeb
        ? t(locationStrings.blockedWebBody)
        : t(locationStrings.blockedNativeBody);

    case "services-disabled":
      if (!isWeb) return t(locationStrings.offBody);
      return isMac
        ? t(locationStrings.unavailableMacBody, { browser })
        : t(locationStrings.unavailableDeviceBody, { browser });

    case "web-insecure":
      return t(locationStrings.secureBody);

    case "web-unsupported":
      return t(locationStrings.unsupportedBody);

    case "error":
      return t(locationStrings.errorBody);

    default:
      return t("onboarding.locationDescription");
  }
}

function locationActionLabel({
  access,
  touched,
  isWeb,
  t,
}: {
  access: LocationAccess;
  touched: boolean;
  isWeb: boolean;
  t: TFunction;
}): string {
  if (!touched) return t("onboarding.locationAction");

  switch (access) {
    case "checking":
    case "permission-required":
      return t("onboarding.locationAction");

    case "permission-blocked":
      return isWeb ? t(locationStrings.checkAgain) : t("common.openSettings");

    case "services-disabled":
      return isWeb
        ? t(locationStrings.checkAgain)
        : t(locationStrings.turnOnLocation);

    default:
      return t("common.tryAgain");
  }
}

function PermissionCard({
  icon,
  title,
  subtitle,
  description,
  actionLabel,
  steps,
  onPress,
  state,
  color,
  tint,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  /** Why this one matters, in a few words. Sits under the title. */
  subtitle?: string;
  description: string;
  actionLabel: string;
  /** Written steps for the cases only the user can clear, on web. */
  steps?: string[];
  onPress: () => void;
  state: State;
  color: string;
  tint: string;
}) {
  const c = useTheme();
  const { t } = useTranslation();
  return (
    <Card style={styles.card}>
      <View style={styles.cardHeader}>
        <View style={[styles.iconWrap, { backgroundColor: tint }]}>
          <Ionicons name={icon} size={22} color={color} />
        </View>
        <View style={styles.cardTitleWrap}>
          <ThemedText variant="subtitle">{title}</ThemedText>
          {subtitle ? (
            <ThemedText variant="caption" style={{ color }}>
              {subtitle}
            </ThemedText>
          ) : null}
        </View>
        {state === "granted" ? (
          <Ionicons name="checkmark-circle" size={24} color={c.success} />
        ) : null}
      </View>
      <ThemedText variant="body" tone="secondary" style={styles.cardDesc}>
        {description}
      </ThemedText>
      {steps?.length ? (
        <View style={styles.steps}>
          {steps.map((step, index) => (
            <View key={step} style={styles.step}>
              <ThemedText variant="caption" tone="muted" style={styles.stepIndex}>
                {index + 1}
              </ThemedText>
              <ThemedText
                variant="caption"
                tone="secondary"
                style={styles.stepText}
              >
                {step}
              </ThemedText>
            </View>
          ))}
        </View>
      ) : null}
      {state === "granted" ? (
        <ThemedText variant="label" tone="success">
          {t("common.allowed")}
        </ThemedText>
      ) : (
        <Button
          // `actionLabel`, with no state-based override on top of it.
          //
          // This used to substitute `common.openSettings` for any blocked state
          // and `common.tryAgain` for any denied one, which silently discarded
          // the label the caller had computed. On web that was the whole answer:
          // no page can open a browser's settings, so `locationActionLabel`
          // returns "Check again" for a blocked permission — and the card
          // replaced it with "Open settings". The press did the right thing
          // (`show-steps` → refreshAccess), so the button re-checked while
          // claiming it would open Safari, and the user reasonably read a
          // silent re-check as a dead button. The card's own step 3 says
          // "Return to Opncue and select Check again", naming a button that was
          // not on screen. Reported on Safari, 2026-09-05.
          //
          // Nothing is lost for notifications: that caller already passes
          // exactly the mapping this override applied, which is what let the
          // duplication sit here unnoticed.
          label={actionLabel}
          variant={state === "idle" ? "primary" : "secondary"}
          size="md"
          onPress={onPress}
        />
      )}
    </Card>
  );
}

const styles = StyleSheet.create({
  body: { flex: 1, gap: Spacing.lg, paddingTop: Spacing.xl },
  header: { gap: Spacing.sm },
  permissionCheck: { minHeight: 260 },
  promptStack: { gap: Spacing.sm },
  card: { gap: Spacing.md },
  cardHeader: { flexDirection: "row", alignItems: "center", gap: Spacing.md },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: Radius.md,
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitleWrap: { flex: 1, gap: 2 },
  cardDesc: { marginBottom: Spacing.xs },
  steps: { gap: Spacing.xs },
  step: { flexDirection: "row", gap: Spacing.sm },
  stepIndex: { fontWeight: "700" },
  stepText: { flex: 1 },
  skipAction: {
    minHeight: 34,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: Spacing.lg,
  },
  skipActionPressed: { opacity: 0.7 },
  skipActionDisabled: { opacity: 0.45 },
  skipText: { textAlign: "center" },
});
